<script>
  import { onMount, tick } from 'svelte'
  import { reduceMotion } from '../lib/motion.js'

  let { open = false, onClose = () => {}, onNavigate = () => {} } = $props()

  let query = $state('')
  let results = $state([])
  let orphanedResults = $state([])
  let orphanedExpanded = $state(false)
  let selectedIndex = $state(0)
  let inputEl = $state(null)
  let debounceTimer = null
  let searched = $state(false)

  const transitionDuration = reduceMotion ? '0ms' : '150ms'
  const scaleTransitionDuration = reduceMotion ? '0ms' : '200ms'

  let totalCount = $derived(
    results.length +
    (orphanedResults.length > 0 ? 1 : 0) +
    (orphanedExpanded ? orphanedResults.length : 0)
  )

  const TOGGLE_INDEX = $derived(results.length)

  async function search(term) {
    if (!term.trim()) {
      results = []
      orphanedResults = []
      searched = false
      return
    }

    const response = await fetch(`/api/neurons/search?q=${encodeURIComponent(term)}`)
    if (response.ok) {
      const data = await response.json()
      results = data.results
      orphanedResults = data.orphaned_results
      selectedIndex = 0
      searched = true
    }
  }

  function handleInput() {
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => search(query), 250)
  }

  function handleKeydown(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      selectedIndex = Math.min(selectedIndex + 1, totalCount - 1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      selectedIndex = Math.max(selectedIndex - 1, 0)
    } else if (event.key === 'Enter' && totalCount > 0) {
      event.preventDefault()
      handleEnter()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      close()
    }
  }

  function handleEnter() {
    if (selectedIndex < results.length) {
      selectResult(results[selectedIndex])
    } else if (orphanedResults.length > 0 && selectedIndex === TOGGLE_INDEX) {
      toggleOrphaned()
    } else if (orphanedExpanded && selectedIndex > TOGGLE_INDEX) {
      selectResult(orphanedResults[selectedIndex - TOGGLE_INDEX - 1])
    }
  }

  function selectResult(result) {
    onNavigate(result)
    close()
  }

  function close() {
    query = ''
    results = []
    orphanedResults = []
    orphanedExpanded = false
    selectedIndex = 0
    searched = false
    onClose()
  }

  function handleBackdropClick(event) {
    if (event.target === event.currentTarget) {
      close()
    }
  }

  function toggleOrphaned() {
    orphanedExpanded = !orphanedExpanded
    if (!orphanedExpanded && selectedIndex > TOGGLE_INDEX) {
      selectedIndex = TOGGLE_INDEX
    }
  }

  $effect(() => {
    if (open) {
      tick().then(() => inputEl?.focus())
    }
  })
</script>

{#if open}
  <div
    data-testid="search-palette-overlay"
    class="fixed inset-0 z-50 flex justify-center"
    style="background: color-mix(in srgb, var(--color-ground) 60%, transparent); backdrop-filter: blur(8px); animation: overlayFadeIn {transitionDuration} ease-out;"
    onclick={handleBackdropClick}
    onkeydown={handleKeydown}
    role="dialog"
    aria-modal="true"
    aria-label="Search neurons"
  >
    <div
      data-testid="search-palette"
      class="search-palette mt-[20vh] h-fit w-full max-w-lg mx-4 rounded-xl overflow-hidden bg-surface-0 border border-border"
      style="box-shadow: 0 8px 40px color-mix(in srgb, var(--color-ground) 50%, transparent), 0 2px 8px color-mix(in srgb, var(--color-ground) 30%, transparent); animation: paletteScaleIn {scaleTransitionDuration} cubic-bezier(0.32, 0.72, 0, 1);"
    >
      <div class="flex items-center gap-3 px-4 py-3.5 border-b border-border">
        <svg class="text-text-muted shrink-0" width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="9" cy="9" r="6"/>
          <path d="M13.5 13.5L17 17"/>
        </svg>
        <input
          bind:this={inputEl}
          bind:value={query}
          oninput={handleInput}
          data-testid="search-input"
          placeholder="Search neurons..."
          class="search-input flex-1 bg-transparent text-lg outline-none text-text border-none"
          autocomplete="off"
        />
      </div>

      {#if searched && results.length === 0 && orphanedResults.length === 0}
        <div class="px-4 py-10 text-center text-text-muted">
          <p data-testid="search-empty" class="text-sm">No neurons found</p>
        </div>
      {:else}
        {#if results.length > 0}
          <ul data-testid="search-results" class="overflow-y-auto max-h-[60vh] py-1">
            {#each results as result, i}
              <li>
                <button
                  data-testid="search-result"
                  class="w-full text-left px-4 py-3 cursor-pointer transition-colors rounded-sm mx-0"
                  class:bg-surface-1={i === selectedIndex}
                  onclick={() => selectResult(result)}
                  onmouseenter={() => selectedIndex = i}
                >
                  <div class="flex items-center gap-2">
                    <span class="font-body text-text">{result.title}</span>
                    {#if result.children_count > 0}
                      <span class="text-xs tabular-nums text-text-muted opacity-60">{result.children_count}</span>
                    {/if}
                  </div>
                  {#if result.ancestors && result.ancestors.length > 0}
                    <div class="text-xs mt-0.5 text-text-muted opacity-70">
                      {result.ancestors.map(a => a.title).join(' \u203A ')}
                    </div>
                  {/if}
                </button>
              </li>
            {/each}
          </ul>
        {/if}

        {#if orphanedResults.length > 0}
          <div class="border-t border-border">
            <button
              data-testid="orphaned-toggle"
              onclick={toggleOrphaned}
              onmouseenter={() => selectedIndex = TOGGLE_INDEX}
              class="w-full flex items-center gap-2 px-4 py-2 text-xs text-text-muted cursor-pointer hover:bg-surface-1 transition-colors"
              class:bg-surface-1={selectedIndex === TOGGLE_INDEX}
            >
              <svg
                class="shrink-0 transition-transform"
                class:rotate-90={orphanedExpanded}
                width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
              >
                <path d="M4 2l4 4-4 4"/>
              </svg>
              {orphanedResults.length} in discarded branches
            </button>

            {#if orphanedExpanded}
              <ul data-testid="orphaned-results" class="py-1 opacity-60">
                {#each orphanedResults as result, i}
                  {@const globalIndex = TOGGLE_INDEX + 1 + i}
                  <li>
                    <button
                      data-testid="search-result"
                      class="w-full text-left px-4 py-3 cursor-pointer transition-colors rounded-sm mx-0"
                      class:bg-surface-1={globalIndex === selectedIndex}
                      onclick={() => selectResult(result)}
                      onmouseenter={() => selectedIndex = globalIndex}
                    >
                      <div class="flex items-center gap-2">
                        <span class="font-body text-text">{result.title}</span>
                        {#if result.children_count > 0}
                          <span class="text-xs tabular-nums text-text-muted opacity-60">{result.children_count}</span>
                        {/if}
                      </div>
                      {#if result.ancestors && result.ancestors.length > 0}
                        <div class="text-xs mt-0.5 text-text-muted opacity-70">
                          {result.ancestors.map(a => a.title).join(' \u203A ')}
                        </div>
                      {/if}
                    </button>
                  </li>
                {/each}
              </ul>
            {/if}
          </div>
        {/if}
      {/if}
    </div>
  </div>
{/if}

<style>
  .search-input:focus {
    border-color: transparent !important;
    box-shadow: none !important;
  }

  @keyframes overlayFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes paletteScaleIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
</style>
